import { useState } from 'react';
import { LoadFailed } from '../components/QueryState';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canLinkPortalOffice, canManageIntegrations, formatNzDate, nzDate } from '@promanage/core';
import { Button, Chip, Field, Input, Panel } from '@promanage/ui';
import * as Dialog from '@radix-ui/react-dialog';
import { TopBar } from '../components/TopBar';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useSession } from '../features/auth/session';
import {
  INTEGRATION_DESCRIPTIONS,
  UPCOMING_INTEGRATIONS,
  type IntegrationLogo,
  type UpcomingIntegration,
} from '../features/integrations/providers';
import { needsStepUp, trpc, type RouterOutputs } from '../lib/trpc';

/** `?connected=trademe` — set by a callback page on its way back here, and nothing else. */
interface IntegrationsSearch {
  connected?: 'trademe' | 'tenancyservices';
}

export const Route = createFileRoute('/settings/integrations/')({
  validateSearch: (search: Record<string, unknown>): IntegrationsSearch =>
    search.connected === 'trademe' || search.connected === 'tenancyservices'
      ? { connected: search.connected }
      : {},
  component: IntegrationsPage,
});

/**
 * The agency's accounts on third-party services — one card per provider this build knows.
 *
 * WHAT A CARD HAS TO MAKE OBVIOUS is WHICH account is connected, not merely that one is. Connecting
 * a staff member's personal Trade Me account instead of the agency's is the quiet mistake here, and
 * the account's own name, beside who connected it and when, is what catches it.
 */
function IntegrationsPage() {
  const session = useSession();
  const { connected } = Route.useSearch();
  const list = useQuery(
    trpc.integrations.list.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load this agency’s integrations' },
    }),
  );
  const mayManage = canManageIntegrations(session?.roles ?? []);
  // Ours alone — see `canLinkPortalOffice`. An agency's administrator never sees the form.
  const mayLinkOffice = canLinkPortalOffice(session?.roles ?? []);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Integrations"
        subtitle="Accounts on other services this agency has connected"
      />

      <SettingsNav />

      {connected ? (
        <Panel className="mb-4 px-5 py-3">
          <p role="status" className="text-[0.875rem] font-medium text-positive">
            {INTEGRATION_DESCRIPTIONS[connected].name} is connected.
          </p>
        </Panel>
      ) : null}

      {list.isError ? (
        <Panel className="p-6">
          <LoadFailed inline retry={() => void list.refetch()} retrying={list.isFetching} />
        </Panel>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {list.isPending ? <div className="h-72 animate-pulse rounded-lg bg-surface-sunk" /> : null}
        {list.data?.map((integration) => (
          <IntegrationCard
            key={integration.provider}
            integration={integration}
            mayManage={mayManage}
            mayLinkOffice={mayLinkOffice}
          />
        ))}
        {UPCOMING_INTEGRATIONS.map((upcoming) => (
          <UpcomingCard key={upcoming.key} upcoming={upcoming} />
        ))}
      </div>

      {!mayManage ? (
        <p className="mt-4 max-w-prose text-[0.75rem] text-ink-subtle">
          Only an agency administrator can connect or disconnect an integration.
        </p>
      ) : null}
    </div>
  );
}

type Integration = RouterOutputs['integrations']['list'][number];

function IntegrationCard({
  integration,
  mayManage,
  mayLinkOffice,
}: {
  integration: Integration;
  mayManage: boolean;
  mayLinkOffice: boolean;
}) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [linking, setLinking] = useState(false);
  const [askingLandlord, setAskingLandlord] = useState(false);
  const described = INTEGRATION_DESCRIPTIONS[integration.provider];
  const connection = integration.connection;

  const start = useMutation(
    trpc.integrations.tradeMe.start.mutationOptions({
      meta: { errorTitle: `Could not start connecting ${described.name}` },
      onSuccess({ authoriseUrl }) {
        // Leaves the console for Trade Me's own sign-in and consent page. It comes back to
        // /settings/integrations/trademe/callback, which finishes the connection.
        window.location.assign(authoriseUrl);
      },
      onError(error) {
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  // A renewal: the landlord ID is already on the connection, so there is nothing to ask first.
  const signInAgain = useMutation(
    trpc.integrations.tenancyServices.start.mutationOptions({
      meta: { errorTitle: `Could not start signing in to ${described.name}` },
      onSuccess({ authoriseUrl }) {
        window.location.assign(authoriseUrl);
      },
      onError(error) {
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  /*
   * A connection made in the other Trade Me environment than this server now uses. It cannot be
   * used here, and saying so is kinder than letting the first listing fail with a 401.
   */
  const otherEnvironment =
    connection !== null &&
    integration.serverEnvironment !== null &&
    connection.environment !== integration.serverEnvironment;

  /*
   * Asked of Trade Me, not read off our row: it turns an account into a rental agent on its own
   * side, and this is where an agency comes to see whether it has. Said here so the first anybody
   * hears of it is not a refused fee quote on a property.
   */
  const account = useQuery(
    trpc.integrations.tradeMe.account.queryOptions(undefined, {
      // Trade Me's question only: an office has no membership type to be the wrong one.
      enabled:
        integration.provider === 'trademe' &&
        integration.available &&
        connection !== null &&
        !otherEnvironment,
      meta: { errorTitle: `Could not check the ${described.name} account` },
    }),
  );
  const cannotListRentals = account.data?.rentalAgent === false;

  return (
    <Panel className="flex flex-col p-4">
      <LogoPlate name={described.name} logo={described.logo} />

      <h2 className="mt-3 text-[0.9375rem] font-bold">{described.name}</h2>
      <p className="mt-0.5 text-[0.8125rem] text-ink-muted">{described.summary}</p>

      <p className="mt-3 flex flex-wrap items-center gap-1.5">
        {!integration.available ? (
          <Chip tone="neutral">Not available</Chip>
        ) : connection ? (
          <>
            <Chip tone="positive">Connected</Chip>
            {connection.environment === 'sandbox' ? <Chip tone="info">Sandbox</Chip> : null}
            {otherEnvironment ? <Chip tone="warn">Other environment</Chip> : null}
            {cannotListRentals ? <Chip tone="warn">Cannot list rentals yet</Chip> : null}
          </>
        ) : (
          <>
            <Chip tone="neutral">Not connected</Chip>
            {integration.serverEnvironment === 'sandbox' ? <Chip tone="info">Sandbox</Chip> : null}
          </>
        )}
      </p>

      {!integration.available ? (
        <p className="mt-2 text-[0.75rem] text-ink-subtle">
          This server has no {described.name} application configured.
        </p>
      ) : null}
      {otherEnvironment ? (
        <p className="mt-2 text-[0.75rem] text-warn">
          This connection was made in the other {described.name} environment and cannot be used
          here.
        </p>
      ) : null}
      {cannotListRentals ? (
        <p className="mt-2 text-[0.75rem] text-warn">
          {described.name} has not set this account up to list rentals from other software, so it
          will refuse a listing sent from ProManage. It asks for five rental listings placed on{' '}
          {described.name} itself first. This updates by itself once it has.
        </p>
      ) : null}

      {connection ? (
        <dl className="mt-3 flex flex-col gap-2">
          {described.connects === 'landlord' ? (
            // Tenancy Services gives the approval no name of its own; the number is the account.
            <Fact label="Landlord ID">
              <span className="font-mono">{connection.accountId}</span>
            </Fact>
          ) : (
            <Fact label={described.accountNoun}>
              {connection.accountName ?? `Unnamed ${described.accountNoun.toLowerCase()}`}
              <span className="ml-2 text-[0.75rem] font-normal text-ink-subtle tabular-nums">
                {described.connects === 'office' ? 'office' : 'member'} {connection.accountId}
              </span>
            </Fact>
          )}
          <Fact label={described.connects === 'office' ? 'Linked by' : 'Connected by'}>
            {connection.connectedByName}
          </Fact>
          <Fact label="Connected on">
            <span className="tabular-nums">
              {formatNzDate(nzDate(new Date(connection.connectedAt)))}
            </span>
          </Fact>
        </dl>
      ) : null}

      <h3 className="mt-3 text-[0.75rem] font-semibold">
        {connection ? 'What ProManage may do on this account' : 'What connecting allows'}
      </h3>
      <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-4 text-[0.75rem] text-ink-muted">
        {described.grants.map((grant) => (
          <li key={grant}>{grant}</li>
        ))}
      </ul>

      {!connection && integration.available && described.connects === 'oauth' ? (
        <p className="mt-2 text-[0.75rem] text-ink-subtle">
          Sign in with the agency’s own {described.name} account, not a personal one — listings
          appear under whichever account approves.
        </p>
      ) : null}
      {/* Nothing for the agency to press: the provider links the office to ProManage on its own
          side, and our staff record which office is whose. Saying who to ask is the whole of it. */}
      {!connection && integration.available && described.connects === 'office' ? (
        <p className="mt-2 text-[0.75rem] text-ink-subtle">
          ProManage support links the agency’s {described.name} office. Send us the office number
          and we arrange it with {described.name}.
        </p>
      ) : null}

      {!connection && integration.available && described.connects === 'landlord' ? (
        <p className="mt-2 text-[0.75rem] text-ink-subtle">
          Sign in with the RealMe login the agency uses on the {described.name} site. Have the
          agency’s landlord ID to hand — it starts LNL.
        </p>
      ) : null}
      {connection && described.connects === 'landlord' ? (
        <p className="mt-2 text-[0.75rem] text-ink-subtle">
          {described.name} approvals are short-lived. If sending a bond says the sign-in has run
          out, sign in again here — the landlord ID is kept.
        </p>
      ) : null}

      {integration.available && connection && mayManage ? (
        <div className="mt-auto flex flex-wrap gap-2 pt-4">
          {described.connects === 'landlord' ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={signInAgain.isPending}
              onClick={() => signInAgain.mutate()}
            >
              {signInAgain.isPending ? `Opening ${described.name}` : 'Sign in again'}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
            Disconnect
          </Button>
        </div>
      ) : null}
      {integration.available && !connection && described.connects === 'landlord' && mayManage ? (
        <div className="mt-auto pt-4">
          <Button size="sm" onClick={() => setAskingLandlord(true)}>
            Connect {described.name}
          </Button>
        </div>
      ) : null}
      {askingLandlord ? (
        <LandlordDialog name={described.name} onClose={() => setAskingLandlord(false)} />
      ) : null}
      {integration.available && !connection && described.connects === 'oauth' && mayManage ? (
        <div className="mt-auto pt-4">
          <Button size="sm" disabled={start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? `Opening ${described.name}` : `Connect ${described.name}`}
          </Button>
        </div>
      ) : null}
      {integration.available && !connection && described.connects === 'office' && mayLinkOffice ? (
        <div className="mt-auto pt-4">
          <Button size="sm" onClick={() => setLinking(true)}>
            Link an office
          </Button>
        </div>
      ) : null}

      {linking ? (
        <LinkOfficeDialog name={described.name} onClose={() => setLinking(false)} />
      ) : null}

      {confirming && connection ? (
        <DisconnectDialog
          provider={integration.provider}
          accountName={
            connection.accountName ??
            `${described.connects === 'landlord' ? 'landlord' : 'member'} ${connection.accountId}`
          }
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </Panel>
  );
}

/**
 * The agency's landlord ID, asked for before it is sent to sign in.
 *
 * Every lodgement names it, the sign-in does not say what it is, and it becomes the connection's
 * account — which cannot be written afterwards. So it is typed here, and the server carries it
 * sealed beside the approval until the agency comes back.
 */
function LandlordDialog({ name, onClose }: { name: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [landlordId, setLandlordId] = useState('');
  const start = useMutation(
    trpc.integrations.tenancyServices.start.mutationOptions({
      meta: { errorTitle: `Could not start connecting ${name}` },
      onSuccess({ authoriseUrl }) {
        // Leaves the console for the RealMe sign-in. It comes back to
        // /settings/integrations/tenancy-services/callback, which finishes the connection.
        window.location.assign(authoriseUrl);
      },
      onError(error) {
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );
  const normalised = landlordId.trim().toUpperCase();
  const ready = /^LNL-\d{8}$/.test(normalised);

  return (
    <Dialog.Root open onOpenChange={(open) => !open && !start.isPending && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-3rem)] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-lg bg-surface p-5 shadow-modal">
          <Dialog.Title className="text-[1.0625rem] font-extrabold tracking-[-0.014em]">
            Connect {name}
          </Dialog.Title>
          <Dialog.Description className="mt-3 text-[0.875rem] text-ink-muted">
            Bonds are lodged under the agency’s landlord ID. Next you sign in with the RealMe login
            the agency uses on the {name} site.
          </Dialog.Description>
          <form
            className="mt-4 flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (ready) start.mutate({ landlordId: normalised });
            }}
          >
            <Field label="Landlord ID" required hint="LNL- and eight digits">
              {(props) => (
                <Input
                  {...props}
                  value={landlordId}
                  className="font-mono"
                  placeholder="LNL-00001234"
                  maxLength={12}
                  onChange={(event) => setLandlordId(event.target.value)}
                />
              )}
            </Field>
            <div className="mt-1 flex gap-2">
              <Button type="submit" disabled={!ready || start.isPending}>
                {start.isPending ? `Opening ${name}` : 'Continue to sign in'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={start.isPending}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Record which office is this agency's — platform staff only; the server refuses anybody else.
 *
 * The number is put to the provider before it is stored, so an office that has not been linked to
 * ProManage on their side is refused here. It cannot be checked that the office is THIS agency's,
 * which is why the dialog asks for the name as well: it is what the agency's administrator will
 * read on this card afterwards, and the one chance for a wrong number to look wrong.
 */
function LinkOfficeDialog({ name, onClose }: { name: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [officeId, setOfficeId] = useState('');
  const [officeName, setOfficeName] = useState('');

  const link = useMutation(
    trpc.integrations.realEstate.link.mutationOptions({
      meta: { errorTitle: `Could not link the ${name} office` },
      onSuccess() {
        void queryClient.invalidateQueries({ queryKey: trpc.integrations.list.queryKey() });
        onClose();
      },
      onError(error) {
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );
  const ready = /^[1-9]\d{0,11}$/.test(officeId.trim()) && officeName.trim() !== '';

  return (
    <Dialog.Root open onOpenChange={(open) => !open && !link.isPending && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-3rem)] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-lg bg-surface p-5 shadow-modal">
          <Dialog.Title className="text-[1.0625rem] font-extrabold tracking-[-0.014em]">
            Link a {name} office
          </Dialog.Title>
          <Dialog.Description className="mt-3 text-[0.875rem] text-ink-muted">
            Listings from this agency will be filed under this office. Check the number against what{' '}
            {name} confirmed for this agency — it cannot be checked from here.
          </Dialog.Description>

          <form
            className="mt-4 flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (ready) link.mutate({ officeId: officeId.trim(), officeName: officeName.trim() });
            }}
          >
            <Field label="Office number" required>
              {(props) => (
                <Input
                  {...props}
                  value={officeId}
                  inputMode="numeric"
                  className="tabular-nums"
                  onChange={(event) => setOfficeId(event.target.value)}
                />
              )}
            </Field>
            <Field label="Office name" required hint={`As ${name} shows it`}>
              {(props) => (
                <Input
                  {...props}
                  value={officeName}
                  maxLength={200}
                  onChange={(event) => setOfficeName(event.target.value)}
                />
              )}
            </Field>
            <div className="mt-1 flex gap-2">
              <Button type="submit" disabled={!ready || link.isPending}>
                {link.isPending ? `Checking with ${name}` : 'Link office'}
              </Button>
              <Button type="button" variant="secondary" onClick={onClose} disabled={link.isPending}>
                Cancel
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** A provider that is coming but has no backend yet — nothing on it can be pressed. */
function UpcomingCard({ upcoming }: { upcoming: UpcomingIntegration }) {
  return (
    <Panel className="flex flex-col p-4">
      <LogoPlate name={upcoming.name} logo={upcoming.logo} />
      <h2 className="mt-3 text-[0.9375rem] font-bold">{upcoming.name}</h2>
      <p className="mt-0.5 text-[0.8125rem] text-ink-muted">{upcoming.summary}</p>
      <p className="mt-3">
        <Chip tone="accent">Coming soon</Chip>
      </p>
    </Panel>
  );
}

function LogoPlate({ name, logo }: { name: string; logo: IntegrationLogo }) {
  return (
    <div className="flex h-16 items-center justify-center rounded-md border border-rule bg-surface px-4">
      {logo.darkSrc ? (
        <>
          <img src={logo.src} alt={`${name} logo`} className="logo-for-light max-h-7 max-w-full" />
          <img
            src={logo.darkSrc}
            alt={`${name} logo`}
            className="logo-for-dark max-h-7 max-w-full"
          />
        </>
      ) : (
        <img src={logo.src} alt={`${name} logo`} className="max-h-7 max-w-full" />
      )}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-[0.8125rem] font-medium">{children}</dd>
    </div>
  );
}

function DisconnectDialog({
  provider,
  accountName,
  onClose,
}: {
  provider: Integration['provider'];
  accountName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const described = INTEGRATION_DESCRIPTIONS[provider];

  const disconnect = useMutation(
    trpc.integrations.disconnect.mutationOptions({
      meta: { errorTitle: `Could not disconnect ${described.name}` },
      onSuccess() {
        void queryClient.invalidateQueries({ queryKey: trpc.integrations.list.queryKey() });
        onClose();
      },
      onError(error) {
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  return (
    <Dialog.Root open onOpenChange={(open) => !open && !disconnect.isPending && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-3rem)] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-lg bg-surface p-5 shadow-modal">
          <Dialog.Title className="text-[1.0625rem] font-extrabold tracking-[-0.014em]">
            Disconnect {described.name}?
          </Dialog.Title>
          <Dialog.Description className="mt-3 text-[0.875rem] text-ink-muted">
            ProManage will stop acting on{' '}
            <strong className="font-semibold text-ink">{accountName}</strong>. Anything already on{' '}
            {described.name} stays there.
          </Dialog.Description>
          <p className="mt-2 text-[0.8125rem] text-ink-muted">{described.revokeHint}</p>

          <div className="mt-4 flex gap-2">
            <Button
              variant="danger"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate({ provider })}
            >
              {disconnect.isPending ? 'Disconnecting' : 'Disconnect'}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={disconnect.isPending}>
              Cancel
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
